<template>
  <VaSidebar
    v-model="writableVisible"
    :width="sidebarWidth"
    :color="'#010D1A'"
    minimized-width="0"
    class="flex flex-col h-full text-white font-sans pb-20 md:pb-0"
  >
    <!-- Menu principal -->
    <div class="space-y-1 pt-4 px-3 flex-grow">
      <div v-for="(route, index) in navigationRoutes.routes" :key="index">
        <RouterLink
          :to="{ name: route.name }"
          class="flex items-center px-4 py-3 rounded-lg transition font-medium text-sm"
          :class="{
            'bg-[#0D5BFF] text-white': isActive(route.name),
            'text-white/80 hover:bg-[#0D5BFF]/10': !isActive(route.name),
          }"
        >
          <component :is="iconMap[route.id]" class="w-5 h-5 mr-3" />
          {{ $t(`sidebar.${route.id}`) }}
        </RouterLink>
      </div>

      <!-- Primezord Button -->
      <div>
        <button
          class="flex items-center w-full px-4 py-3 rounded-lg transition font-medium text-sm"
          :class="{
            'bg-[#0D5BFF] text-white': isActive('primezord'),
            'text-white/80 hover:bg-[#0D5BFF]/10': !isActive('primezord'),
          }"
          @click="handlePrimezord"
        >
          <CpuChipIcon class="w-5 h-5 mr-3" />
          {{ $t('sidebar.primezord') }}
        </button>
      </div>
    </div>

    <!-- Rodapé fixo -->
    <div class="px-3 pb-4 space-y-4">
      <div class="md:hidden bg-[#0A1121] text-white rounded-lg p-3 text-center text-sm border border-[#1F2436]">
        <div class="text-xs text-gray-400 mb-1">{{ $t('sidebar.saldo_disponivel') }}</div>
        <div class="text-[#00FF80] font-bold text-base">
          {{ Number(appStore.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
        </div>
      </div>
      <button
        class="w-full flex items-center justify-center text-white border border-[#0D5BFF] py-2 rounded-md hover:bg-[#0D5BFF] transition"
        @click="redirectToBroker"
      >
        <ArrowLeftIcon class="w-4 h-4 mr-2" />
        {{ $t('sidebar.voltar_corretora') }}
      </button>

      <!-- Seletor de idioma -->
      <div class="w-full">
        <VaSelect
          v-model="language"
          :options="[
            { label: '🇧🇷 Português', value: 'br' },
            { label: '🇺🇸 English', value: 'gb' },
            { label: '🇪🇸 Español', value: 'es' },
          ]"
          :track-by="'value'"
          :text-by="'label'"
          :value-by="'value'"
          :clearable="false"
          size="small"
          class="w-full"
          background="background-element"
          color="#0D5BFF"
          text-color="white"
        />
      </div>

      <div class="bg-[#0A1121] text-white rounded-lg p-3 text-center text-sm">
        <div class="flex items-center justify-center gap-2 text-xs text-gray-400">
          <ClockIcon class="w-4 h-4" />
          {{ $t('sidebar.tempo_sessao') }}
        </div>
        <div class="font-bold text-base mt-1">4 horas e 58 minutos</div>
      </div>
    </div>
  </VaSidebar>

  <!-- Modal Primezord -->
  <PrimezordModal v-if="modalPrimezord" />
</template>

<script setup lang="ts">
import type { FunctionalComponent, HTMLAttributes, VNodeProps } from 'vue'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '../../stores/integration'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ClockIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  LockClosedIcon,
  Squares2X2Icon,
} from '@heroicons/vue/24/solid'
import PrimezordModal from '@/components/modal/PrimezordModal.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '@/stores/global-store'
import { VaSelect } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'

const appStore = useAppStore()
const router = useRouter()
const { isSidebarMinimized } = storeToRefs(useGlobalStore())

const modalPrimezord = ref(false)

const { t: $t, locale } = useI18n()

const language = computed({
  get: () => locale.value,
  set: (val) => {
    locale.value = val
  },
})

const props = defineProps({
  visible: { type: Boolean, default: true },
  mobile: { type: Boolean, default: false },
})

const route = useRoute()

const writableVisible = computed({
  get: () => !isSidebarMinimized.value,
  set: (v: boolean) => {
    isSidebarMinimized.value = !v
  },
})

const sidebarWidth = computed(() => (props.mobile ? '100vw' : '260px'))

const redirectToBroker = () => {
  window.location.href = 'https://traderoom.primeoptions.io/'
}

const isActive = (routeName: string) => route.name === routeName

const handlePrimezord = async () => {
  await appStore.fetchUser()
  if (appStore.hasBot) {
    router.push({ name: 'primezord' })
  } else {
    modalPrimezord.value = true
  }
}

const navigationRoutes = {
  routes: [
    { name: 'dashboard', id: 'dashboard' },
    { name: 'stake', id: 'stake' },
    { name: 'history', id: 'history' },
    { name: 'network', id: 'network' },
    { name: 'copytrade', id: 'copytrade' },
    { name: 'operations', id: 'operations' },
    { name: 'primevault', id: 'primevault' },
  ],
}

const iconMap: Record<string, FunctionalComponent<HTMLAttributes & VNodeProps>> = {
  dashboard: Squares2X2Icon,
  stake: CurrencyDollarIcon,
  history: ArrowPathIcon,
  network: GlobeAltIcon,
  copytrade: DocumentDuplicateIcon,
  primevault: LockClosedIcon,
  operations: Squares2X2Icon,
  primezord: CpuChipIcon,
}
</script>

<style scoped>
:deep(.va-select__anchor) {
  background-color: #0a1121;
  border: 1px solid #1f2436;
  color: white;
}
</style>
